import { Camera, Engine, RenderTargetTexture, Scene, Tools } from '@babylonjs/core';
import { AppContext } from 'Api/AppContext';
import { AssetItemType, JsonValue, SmartObjectDto } from 'Api/Contracts/Dtos';
import { ISmartObjectService } from 'Api/Contracts/Interfaces';
import { AssetFile, AssetHotspot, AssetItem, AssetPanoramaGroup, AssetPanoramaVersion, FileMap, Navigation, Project, ProjectMap } from 'Api/Services';
import { SmartObjectHotspotsSpecification } from 'App/Areas/Explore/Components/Specifications';
import EplumeDialog from 'App/Areas/Explore/Dialogs/EplumeDialog.vue';
import FolderWithCommentDialog from 'App/Areas/Explore/Dialogs/FolderWithCommentDialog.vue';
import OranoEquipmentDialog from 'App/Areas/Explore/Dialogs/OranoEquipmentDialog.vue';
import OranoPrsDialog from 'App/Areas/Explore/Dialogs/OranoPrsDialog.vue';
import VoSmartObjectDetailsDialog from 'App/Areas/Explore/Dialogs/VoSmartObjectDetailsDialog.vue';
import { IDiaporamaDialogContext } from 'App/Areas/Shares/Dialogs/IDiaporamaDialogContext';
import VoDiaporamaDialog from 'App/Areas/Shares/Dialogs/VoDiaporamaDialog.vue';
import VoIFrameDialog from 'App/Areas/Shares/Dialogs/VoIFrameDialog.vue';
import { IPlayerContext } from 'App/Areas/Shares/IPlayerContext';
import { getPanoramaMobileUrl, getPanoramaUrl, getVideoUrl, makeLocalHotspot } from 'App/Helpers/AssetHelper';
import { CoordinatesHelper } from 'App/Helpers/CoordinatesHelper';
import { IVisonUriService } from 'App/Services/UriServices/Core';
import { SpecificationCollection } from 'App/Utils';
import { filterRecursive } from 'Commons/Common';
import { ICamera } from 'Framework/Components/Controls/XView/Cameras';
import { ICartesianCoordinates, IConfiguration, ICoordinates, IHotspot, IIconProvider, IPanoramaConfiguration, IPanoramaLevel, ISphericalCoordinates, RenderType } from 'Framework/Components/Controls/XView/Core';
import XView from 'Framework/Components/Controls/XView/XView.vue';
import { UIElement } from 'Framework/Components/UIElement';
import { EventHandler } from 'Framework/Extensions/CoreJsExtensions';
import { MathHelper } from 'Framework/Helpers/MathHelper';
import { UrlHelper } from 'Framework/Helpers/UrlHelper';
import { DialogResult, IDialogService } from 'Framework/Services/IDialogService';
import { Dictionary, groupBy, isNumber, isString } from 'lodash';
import ISpecification from 'ts-specification/dist/ISpecification';

class PanoramaConfiguration implements IPanoramaConfiguration {
    public constructor(getHotspots: () => Array<IHotspot>) {
        this._getHotspots = getHotspots;
    }

    public Url: string;
    public MaxLevel: number;
    public IsMultiResolution: boolean;
    public Levels: IPanoramaLevel[];
    public startOrientation: ICoordinates;
    public RenderType: RenderType;
    public MaxFov?: number;
    public MinFov?: number;
    public Fov?: number;
    public iconProvider: IIconProvider;

    public get hotspots(): Array<IHotspot<any>> {
        return this._getHotspots();
    }

    private readonly _getHotspots: () => Array<IHotspot>;
}

export class PlayerContext implements IPlayerContext {
    constructor(
        player: UIElement,
        appContext: AppContext,
        uriService: IVisonUriService,
        dialogService: IDialogService,
        iconProvider: IIconProvider,
        smartObjectService: ISmartObjectService,
        project: Project = null
    ) {
        this._player = player;
        this.currentProject = project;
        this._appContext = appContext;
        this.uriService = uriService;
        this._dialogService = dialogService;
        this._iconProvider = iconProvider;
        this._smartObjectService = smartObjectService;

        this.hotspotsFilters = new SpecificationCollection();
        this.hotspotsFilters.onUpdate.subscribe(this._onUpdateFiltersHandler);
    }

    public getDefaultPanoramaVersion(panoramaGroup: AssetPanoramaGroup): AssetPanoramaVersion {
        return panoramaGroup.getDefaultPanoramaVersion(this.currentPanorama?.name);
    }

    public goToPanorama(panoramaOrAssetItemId: AssetPanoramaGroup | number | string, startOrientation: ICoordinates = null, fov?: number): void {
        if (this.panoramas.length == 0) {
            return;
        }

        let panoramaGroup: AssetPanoramaGroup = null;

        if (panoramaOrAssetItemId instanceof AssetPanoramaGroup) {
            panoramaGroup = panoramaOrAssetItemId;
        }
        else if (isNumber(panoramaOrAssetItemId)) {
            panoramaGroup = this.panoramas.find(item => item.assetItemId == panoramaOrAssetItemId);
        }
        else if (isString(panoramaOrAssetItemId)) {
            const isRegex = panoramaOrAssetItemId.startsWith('^') && panoramaOrAssetItemId.endsWith('$');

            if (isRegex) {
                const regex = new RegExp(panoramaOrAssetItemId);

                panoramaGroup = [...this.panoramas]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .find(item => regex.test(item.name));
            }
            else {
                panoramaGroup = this.panoramas.find(item => item.name == panoramaOrAssetItemId);
            }
        }
        else {
            console.error('Wrong input type for parameter: \'panoramaOrAssetItemId\' ');
        }

        if (!panoramaGroup) {
            this._raisePanoramaNotFound();
            return;
        }

        const destinationVersion = this.getDefaultPanoramaVersion(panoramaGroup);

        let orientation: ISphericalCoordinates = null;

        if (startOrientation) {
            orientation = MathHelper.isSphericalCoordinates(startOrientation)
                ? { ...startOrientation }
                : MathHelper.toSphericalCoordinates(startOrientation as ICartesianCoordinates);

            orientation = CoordinatesHelper.changeAzimuthBase(orientation, 0, destinationVersion.offset);
        }

        this._loadPanorama(destinationVersion, orientation, fov);
    }

    public loadNavigation(navigation: Navigation): void {
        this.currentNavigation = navigation;
    }

    public goToPanoramaVersion(panoramaOrAssetItemId: AssetPanoramaVersion | number, startOrientation: ICoordinates = null, fov?: number): void {
        if (this.panoramas.length == 0) {
            return;
        }

        if (panoramaOrAssetItemId instanceof AssetPanoramaVersion) {
            this._loadPanorama(panoramaOrAssetItemId, startOrientation, fov);
        }
        else {
            let assetItem = this.panoramas
                .flatMap(g => g.children as Array<AssetPanoramaVersion>)
                .find(apv => apv.assetItemId == panoramaOrAssetItemId);

            this._loadPanorama(assetItem, startOrientation, fov);
        }
    }

    private _loadPanorama(panorama: AssetPanoramaVersion, startOrientation: ICoordinates = null, fov?: number): void {
        if (!panorama) {
            this.currentPanorama = null;
            this.viewerConfiguration = null;

            this._raisePanoramaNotFound();
            return;
        }

        //TODO : Implement action before load panorama
        UIElement.Bus.$emit('player-content-loading', { sender: this, args: panorama });

        if (this.currentPanorama != panorama) {
            this.currentPanorama = panorama;

            if (this.currentNavigation) {
                let map = this.currentNavigation.maps.find(map => {
                    return map instanceof FileMap
                        && map.children.findIndex(link => (link as AssetHotspot).assetItemLinkedId == panorama.parentId) >= 0
                }) as FileMap;
                this.currentMap = map;
            }
            else {
                this.currentMap = null;
            }

            this.viewerConfiguration = this.buildProjectConfiguration(panorama, startOrientation, fov);
        }
        else {
            let coordinates: ICartesianCoordinates = panorama.startOrientation ?? null;

            if (MathHelper.isCartesianCoordinates(startOrientation)) {
                coordinates = startOrientation;
            }
            else if (MathHelper.isSphericalCoordinates(startOrientation)) {
                coordinates = MathHelper.toCartesianCoordinates(startOrientation);
            }

            UIElement.Bus.$emit(
                'look-to-event-bus',
                this,
                coordinates
            );

            if (fov) {
                UIElement.Bus.$emit(
                    'set-fov',
                    { sender: this, fov: fov }
                );
            }
        }

        let onLoadedFunctionToParse = panorama.events.onLoaded ?? panorama.parent.events.onLoaded;
        let onLoadedFunction = this.parseFunction(onLoadedFunctionToParse, 'assetPanoramaGroup', 'assetPanoramaVersion');
        onLoadedFunction?.(panorama.parent, panorama);

        UIElement.Bus.$emit('player-content-loaded', { sender: this, args: panorama });
    }

    public openAsset(assetId: number, startAssetId?: number): void {
        let assetItem: AssetItem | SmartObjectDto = this.findAssetFileFromAssetId(assetId)
            ?? this.findSmartObject(assetId);

        if (assetItem instanceof AssetFile) {
            if (assetItem.type == AssetItemType.Directory) {
                let assetItems = (this.findAssetFileFromAssetFolderId(assetId, true) as Array<AssetFile>)
                    .sort((a, b) => a.name.localeCompare(b.name));

                if (assetItems?.length > 0) {
                    let assetFile = assetItems.find(item => item.assetItemId == startAssetId) || assetItems[0];

                    if (assetFile instanceof AssetFile) {
                        let dataContext: IDiaporamaDialogContext = {
                            token: this.validatedToken,
                            selectedItem: assetFile.file,
                            itemsSource: assetItems.map(item => item.file)
                        };

                        const _ = this._dialogService.openAsync(
                            'Shares',
                            VoDiaporamaDialog,
                            dataContext,
                            {
                                width: this._appContext.settings.dialogs.large.width,
                                height: this._appContext.settings.dialogs.large.height,
                            }
                        );
                    }
                }
            }
            else {
                let file = assetItem.file;

                if (file) {
                    let dataContext: IDiaporamaDialogContext = {
                        token: this.validatedToken,
                        selectedItem: file,
                        itemsSource: [file]
                    };

                    const _ = this._dialogService.openAsync(
                        'Shares',
                        VoDiaporamaDialog,
                        dataContext,
                        {
                            width: this._appContext.settings.dialogs.large.width,
                            height: this._appContext.settings.dialogs.large.height,
                        }
                    );
                }
            }
        }
        else if (assetItem instanceof SmartObjectDto) {
            const _ = this.openSmartObjectDetailsDialogAsync(assetItem);
        }
    }

    public openFolderWithComment(assetId: number, comment?: string): void {
        let assetItem = this.findAssetFileFromAssetId(assetId);
        if (assetItem) {
            if (assetItem.type == AssetItemType.Directory) {
                const _ = this._dialogService.openAsync(
                    'Explore',
                    FolderWithCommentDialog,
                    {
                        playerContext: this,
                        assetItem: assetItem,
                        comment: comment
                    },
                    {
                        width: this._appContext.settings.dialogs.large.width,
                        height: this._appContext.settings.dialogs.large.height,
                    }
                );
            }
        }
    }

    private _stopAudio(): void {
        this.currentMediaPlaying?.stop();
        this.currentMediaPlaying = null;

        UIElement.Bus.$off('player-content-loading', this._stopAudio);
    }

    public playAudio(assetId: number): void {
        if (this.currentMediaPlaying?.currentTime) {
            if (this.currentMediaPlaying.getAttribute('asset-item-id') == assetId?.toString()) {
                this.currentMediaPlaying.stop();
                return;
            }
            else {
                this._stopAudio();
            }
        }

        let assetItem = this.findAssetFileFromAssetId(assetId);
        if (assetItem) {
            if (assetItem instanceof AssetFile) {
                let uri = this.uriService.getAbsoluteUri(`/${this._appContext.codeCulture}/asset/audio/${assetItem.file.guid}`);
                uri = UrlHelper.addUrlParameter(uri, 'token', this.validatedToken);

                this.currentMediaPlaying = new Audio(uri);
                this.currentMediaPlaying.setAttribute('asset-item-id', assetItem.assetItemId.toString());
                this.currentMediaPlaying.play();

                UIElement.Bus.$once('player-content-loading', this._stopAudio.bind(this));
            }
        }
    }

    public openPopup(url: string, title: string, w: number, h: number, inNewWindow: boolean = false): void {
        if (inNewWindow) {
            // Fixes dual-screen position Most browsers      Firefox
            const dualScreenLeft = window.screenLeft ?? window.screenX;
            const dualScreenTop = window.screenTop ?? window.screenY;

            const width: number = window.innerWidth
                || document.documentElement.clientWidth
                || screen.width;

            const height: number = window.innerHeight
                || document.documentElement.clientHeight
                || screen.height;

            let left = ((width / 2) - (w / 2)) + dualScreenLeft;
            let top = ((height / 2) - (h / 2)) + dualScreenTop;
            let newWindow = window.open(url, title, 'scrollbars=yes, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left);

            // Puts focus on the newWindow
            if (window.focus) {
                newWindow.focus();
            }
        }
        else {
            const _ = this._dialogService.openAsync(
                'Shares',
                VoIFrameDialog,
                {
                    title: title,
                    url: url
                },
                {
                    width: this._appContext.settings.dialogs.large.width,
                    height: this._appContext.settings.dialogs.large.height,
                }
            );
        }
    }

    public createMail(recipient: string, cc: string, subject: string, body: string): void {
        let bodyEncoded = encodeURIComponent(body);
        let subjectEncoded = encodeURIComponent(subject);

        let uri = '?';
        if (cc != '') {
            uri += 'cc=' + cc + '&';
        }
        if (subject != '') {
            uri += 'subject=' + subjectEncoded + '&';
        }
        if (body != '') {
            uri += 'body=' + bodyEncoded;
        }

        let mailTo = 'mailto:' + recipient + uri;

        window.location.href = mailTo;
    }

    public eplumeElement(wiwId: string, ewattchId: string): void {
        if (!wiwId && !ewattchId) {
            return;
        }

        //Vérifie si vis-on est intégré dans une iframe
        if (window.parent) {
            let wiwInputId = UrlHelper.getUrlParameter('wiwinput');

            if (wiwInputId) {
                try {
                    parent.window.postMessage(JSON.stringify({ entityId: wiwId }), this._getParentOrigin());
                    return;
                }
                catch (err) {
                    console.log('IFrame WIW postMessage error : ', err);
                }
            }
        }

        const _ = this._dialogService.openAsync(
            'Explore',
            EplumeDialog,
            {
                wiwId: wiwId,
                ewattchId: ewattchId
            },
            {
                width: this._appContext.settings.dialogs.large.width,
                height: this._appContext.settings.dialogs.large.height,
            }
        );
    }

    public async openSmartObjectDetailsDialogAsync(smartObject: SmartObjectDto | number): Promise<void> {
        const smartObjectDto: SmartObjectDto = typeof smartObject === 'number'
            ? await this._smartObjectService.getObjectAsync(
                smartObject,
                null,
                this.validatedToken)
            : smartObject;

        const dialogResult: DialogResult = await this._dialogService.openAsync(
            'Explore',
            VoSmartObjectDetailsDialog,
            {
                smartObject: smartObjectDto,
                projectId: this.currentProject.id
            },
            {
                width: this._appContext.settings.dialogs.medium.width,
                height: this._appContext.settings.dialogs.medium.height,
            });

        if (dialogResult == DialogResult.None) {
            this._updateCurrentHotspotLinkedToSmartObject(smartObjectDto);
        }
    }

    public openOranoEquipement(unit: string, ecode: string) {
        if (!unit || !ecode) {
            return;
        }

        const _ = this._dialogService.openAsync(
            'Explore',
            OranoEquipmentDialog,
            {
                unit: unit,
                ecode: ecode
            },
            {
                width: this._appContext.settings.dialogs.large.width,
                height: this._appContext.settings.dialogs.large.height,
            }
        );
    }

    public openOranoPrs(baseUriPrs: string, workshopName: string, localName: string) {
        if (!baseUriPrs || !workshopName || !localName) {
            return;
        }

        const _ = this._dialogService.openAsync(
            'Explore',
            OranoPrsDialog,
            {
                baseUriPrs: baseUriPrs,
                workshopName: workshopName,
                localName: localName
            },
            {
                width: this._appContext.settings.dialogs.large.width,
                height: this._appContext.settings.dialogs.large.height,
            }
        );
    }

    public openScanEdit(rfo: string): void {
        this._player.$el.dispatchEvent(new CustomEvent('hotspotclicked', { detail: rfo }));
    }

    public async generateScreenshotsAsync(
        xview: XView,
        hotspots: Array<AssetHotspot>
    ): Promise<Array<string>> {
        const screenshots: Array<string> = [];

        const currentPanorama: AssetPanoramaVersion = this.currentPanorama;
        const currentOrientation: ICoordinates = this.currentOrientation;
        const currentFocale: number = this.currentFocale;

        const mainScene: Scene = xview.viewer.scenes[0];
        const engine: Engine = mainScene.getEngine();

        const camera: Camera & ICamera = xview.viewer.camera;

        const textureSize = 250;
        const fov = 0.6;

        const renderTargetTexture = new RenderTargetTexture(
            'screenshot',
            textureSize,
            mainScene);

        camera.outputRenderTarget = renderTargetTexture;

        const openPanoramaAsync = (panoramaId: number): Promise<void> => new Promise(
            resolve => {
                if (this.currentPanorama.assetItemId == panoramaId) {
                    resolve();
                    return;
                }

                UIElement.Bus.$once('configuration-changed', () => resolve());
                this.goToPanoramaVersion(panoramaId, null, fov);
            });

        const lookToAsync = (coordinates: ISphericalCoordinates): Promise<void> => new Promise(
            resolve => {
                UIElement.Bus.$once('player-content-loaded', () => resolve());
                this.goToPanoramaVersion(this.currentPanorama.assetItemId, coordinates, fov);
            });

        const waitSceneIsReadyAsync = (scene: Scene): Promise<void> => new Promise<void>(
            resolve => scene.executeWhenReady(() => resolve(null), true));

        const waitScenesAreReadyAsync = async (): Promise<void> => {
            await Promise.all(engine.scenes.map(waitSceneIsReadyAsync));
        }

        const waitEndOfFrameAsync = (): Promise<void> => new Promise<void>(
            resolve => engine.onEndFrameObservable.addOnce(() => resolve()));

        const groupedHotspots: Dictionary<Array<AssetHotspot>> = groupBy(
            hotspots,
            h => h.parentId);

        for (const parentId in groupedHotspots) {
            const panoramaId = Number(parentId);

            await openPanoramaAsync(panoramaId);

            for (const hotspot of groupedHotspots[parentId]) {
                const [coordinates] = hotspot.coordinates as Array<ISphericalCoordinates>;

                await lookToAsync(coordinates);
                await waitScenesAreReadyAsync();
                await waitEndOfFrameAsync();

                const data: ArrayBufferView = await renderTargetTexture.readPixels();

                const screenshot: string | ArrayBuffer = await Tools.DumpDataAsync(
                    textureSize,
                    textureSize,
                    data,
                    'image/png',
                    null,
                    true,
                    false);

                if (typeof screenshot === 'string') {
                    screenshots.push(screenshot);
                }
            }
        }

        camera.outputRenderTarget.dispose();

        //Reset viewer to previous state
        this.goToPanoramaVersion(
            currentPanorama,
            currentOrientation,
            currentFocale);

        return screenshots;
    }

    public findPanoramaFileFromAssetId(assetItemId: number): AssetPanoramaVersion {
        if (this.panoramas) {
            return this.getDefaultPanoramaVersion(
                this.panoramas.find(item => item.assetItemId == assetItemId)
            );
        } else {
            return null;
        }
    }

    public findAssetFileFromAssetId(assetId: number): AssetItem {
        if (this.assets) {
            return this.assets.find(item => item.assetItemId == assetId);
        } else {
            return null;
        }
    }

    public findSmartObject(assetId: number): SmartObjectDto {
        return this.smartObjects?.find(item => item.assetItemId == assetId);
    }

    public findAssetFileFromAssetFolderId(assetId: number, includeSubFolder: boolean = false): Array<AssetItem> {
        if (this.assets) {
            if (includeSubFolder) {
                return [...filterRecursive(item => item.parentId == assetId, this.assets, 'children', () => true)];
            } else {
                return this.assets.filter(item => item.parentId == assetId);
            }
        } else {
            return [];
        }
    }

    private _getParentOrigin(): string {
        const locationAreDistinct = (window.location !== window.parent.location);
        const parentOrigin = ((locationAreDistinct ? document.referrer : document.location) || '').toString();

        if (parentOrigin) {
            return new URL(parentOrigin).origin;
        }

        const currentLocation = document.location;

        return currentLocation.ancestorOrigins?.[0] ?? '';
    }

    public *getHotposts(panorama: AssetPanoramaVersion): IterableIterator<IHotspot> {
        const panoramaGroup = panorama.parent as AssetPanoramaGroup;
        const groupHotspots = panoramaGroup.hotspots;

        const parseEventIfExists = (hotspot: IHotspot): void => {
            const onclickCallback = this.parseFunction(
                hotspot.events?.onClick,
                'hotspot',
                'hotspotElement');

            if (onclickCallback) {
                hotspot.onclick = onclickCallback;
            }
        }

        for (const hotspot of groupHotspots) {
            if (this._aggregatedFilter && !this._aggregatedFilter.isSatisfiedBy(hotspot)) {
                continue;
            }

            let hs = makeLocalHotspot(hotspot, panorama);
            parseEventIfExists(hs);

            yield hs;
        }

        for (const hotspot of panorama.hotspots) {
            if (this._aggregatedFilter && !this._aggregatedFilter.isSatisfiedBy(hotspot)) {
                continue;
            }

            parseEventIfExists(hotspot);

            yield hotspot;
        }
    }

    private buildProjectConfiguration(panorama: AssetPanoramaVersion, startOrientation: ICoordinates = null, fov?: number): IConfiguration {
        let url: string = null;

        if (this.isStandalone) {
            url = this.uriService.getAbsoluteUri(
                panorama.renderType == RenderType.Cube
                    ? getPanoramaMobileUrl(this._appContext.codeCulture, panorama.file.guid)
                    : getVideoUrl(this._appContext.codeCulture, panorama.file.guid)
            );
        }
        else {
            url = this.uriService.getAbsoluteUri(
                panorama.renderType == RenderType.Cube
                    ? getPanoramaUrl(this._appContext.codeCulture, panorama.file.guid)
                    : getVideoUrl(this._appContext.codeCulture, panorama.file.guid)
            );

            url = UrlHelper.addUrlParameter(url, 'token', this.validatedToken);
        }

        const hotspotsFunc = () => Array.from(this.getHotposts(panorama));

        return Object.assign(new PanoramaConfiguration(hotspotsFunc), {
            RenderType: panorama.renderType,
            MaxLevel: panorama.level,
            Url: url,
            Fov: fov ?? 80 / 180 * Math.PI,
            Levels: [...this.levelGenerator(panorama.level, !this.isStandalone)],
            startOrientation: startOrientation || panorama.startOrientation,
            iconProvider: this._iconProvider
        });
    }

    private *levelGenerator(maxLevel: number, constantTileSize: boolean = true): IterableIterator<IPanoramaLevel> {
        for (let level: number = 1; level <= maxLevel; level++) {
            let size: number = Math.pow(2, level - 1);
            let sizeOfImage = size * 512;
            yield {
                ImageWidth: sizeOfImage,
                ImageHeight: sizeOfImage,
                TileSize: constantTileSize ? 512 : sizeOfImage
            };
        }
    }

    public bind(func: Function): Function {
        return func.bind(this);
    }

    public parseFunction(literalFunction: string, ...args: string[]): Function {
        let func = this._functionMap.get(literalFunction);

        if (func) {
            return func;
        }

        try {
            func = new Function(...args, literalFunction).bind(this);

            this._functionMap.set(literalFunction, func);

            return func;
        } catch (e) {
            console.log('Vison : Parse function error', e);
        }

        return null;
    }

    private _updateCurrentHotspotLinkedToSmartObject(smartObjectDto: SmartObjectDto): void {
        if (this.currentPanorama == null || this.currentPanorama.hotspots.length == 0) {
            return;
        }

        const propertyName: string = smartObjectDto.displayPropertyPath?.substring(1);

        if (!Object.hasOwn(smartObjectDto.dataSource, propertyName)) {
            return;
        }

        const name: JsonValue = smartObjectDto.dataSource[propertyName];

        if (typeof name != 'string') {
            return;
        }

        const hotspotsToUpdate: Array<IHotspot> = this.panoramas
            .flatMap(g => g.children as Array<AssetPanoramaVersion>)
            .flatMap(pv => pv.hotspots)
            .filter(hs => hs.assetItemLinkedId == smartObjectDto.assetItemId
                && hs.name != name);

        if (hotspotsToUpdate.length == 0) {
            return;
        }

        for (const hotspot of hotspotsToUpdate) {
            //ignore custom tooltip
            if (hotspot.content.tooltip == hotspot.name) {
                hotspot.content.tooltip = name;
            }

            hotspot.name = name;
        }

        const oldSpecification: ISpecification<AssetHotspot> = this.hotspotsFilters
            .specifications
            .find(s => s instanceof SmartObjectHotspotsSpecification);

        if (!oldSpecification) {
            UIElement.Bus.$emit('refresh-hotspots');
            return;
        }

        const newHotspotFilterNames: Array<string> = [
            ...this.currentPanorama.hotspots
                .filter(hs => !hotspotsToUpdate.includes(hs))
                .map(hs => hs.name),
            name
        ];

        this.hotspotsFilters.update(
            new SmartObjectHotspotsSpecification(newHotspotFilterNames),
            oldSpecification);
    }

    private _onUpdateFilters(newValue: ReadonlyArray<ISpecification<AssetHotspot>>): void {
        this._aggregatedFilter = newValue.length == 0
            ? null
            : newValue.reduce((p, c) => p.and(c));

        UIElement.Bus.$emit('refresh-hotspots');
    }

    private _raisePanoramaNotFound(): void {
        UIElement.Bus.$emit('player-error-raised', {
            sender: this,
            args: {
                code: 'PlayerContext.PanoramaNotFound'
            }
        });
    }

    public token: string = null;
    public validatedToken: string = null;
    public isStandalone: boolean = false;
    public assets: Array<AssetItem> = [];
    public panoramas: Array<AssetPanoramaGroup> = [];
    public navigations: Array<Navigation> = [];
    public smartObjects: Array<SmartObjectDto> = [];

    public currentProject: Project = null;
    public currentPanorama: AssetPanoramaVersion = null;
    public currentNavigation: Navigation = null;
    public viewerConfiguration: IConfiguration = null;
    public currentMap: ProjectMap = null;
    public currentOrientation: ICoordinates = null;
    public currentFocale: number = null;
    public isPanoramaHasPointCloud: boolean = false;
    public isDropAllowed: boolean = false;
    public currentMediaPlaying: HTMLAudioElement | HTMLVideoElement = null;
    public readonly hotspotsFilters: SpecificationCollection<AssetHotspot>;

    public uriService: IVisonUriService;

    private readonly _appContext: AppContext;

    private readonly _dialogService: IDialogService;

    private readonly _smartObjectService: ISmartObjectService;

    private readonly _iconProvider: IIconProvider;

    private readonly _onUpdateFiltersHandler: EventHandler
        = sender => this._onUpdateFilters(
            (sender as SpecificationCollection<AssetHotspot>).specifications);

    private readonly _player: UIElement;
    private readonly _functionMap: Map<string, Function> = new Map<string, Function>();

    private _aggregatedFilter: ISpecification<AssetHotspot> = null;
}
